import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, RTL_LANGUAGES, normalizeSupportedLanguage } from '../i18n';

type LanguageProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

type LanguageProviderState = {
  language: string;
  setLanguage: (language: string) => void;
  isRTL: boolean;
  languages: typeof SUPPORTED_LANGUAGES;
};

const initialState: LanguageProviderState = {
  language: 'en',
  setLanguage: () => null,
  isRTL: false,
  languages: SUPPORTED_LANGUAGES,
};

const LanguageProviderContext = createContext<LanguageProviderState>(initialState);

export function LanguageProvider({
  children,
  storageKey = 'omniscribe-language',
}: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<string>(
    () => normalizeSupportedLanguage(localStorage.getItem(storageKey) || i18n.resolvedLanguage || i18n.language || navigator.language)
  );

  const isRTL = RTL_LANGUAGES.includes(language);

  const setLanguage = useCallback((newLanguage: string) => {
    const normalizedLanguage = normalizeSupportedLanguage(newLanguage);
    localStorage.setItem(storageKey, normalizedLanguage);
    setLanguageState(normalizedLanguage);
    i18n.changeLanguage(normalizedLanguage);
  }, [i18n, storageKey]);

  useEffect(() => {
    const root = window.document.documentElement;
    
    root.setAttribute('lang', language);
    root.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    
    if (isRTL) {
      root.classList.add('rtl');
      root.classList.remove('ltr');
    } else {
      root.classList.add('ltr');
      root.classList.remove('rtl');
    }
  }, [language, isRTL]);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const value = {
    language,
    setLanguage,
    isRTL,
    languages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageProviderContext.Provider value={value}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);

  if (context === undefined)
    throw new Error('useLanguage must be used within a LanguageProvider');

  return context;
};
